import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { postData } from '../../../services/apiService';

const ForgetPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const navigate = useNavigate();

  // Generate bubbles matching login page
  useEffect(() => {
    const generateBubbles = () => {
      const newBubbles = Array.from({ length: 15 }, (_, index) => ({
        id: index,
        size: Math.random() * 60 + 20,
        left: Math.random() * 100,
        animationDuration: Math.random() * 20 + 10,
        animationDelay: Math.random() * 5,
        opacity: Math.random() * 0.3 + 0.1,
        blur: Math.random() * 10 + 5,
      }));
      setBubbles(newBubbles);
    };

    generateBubbles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShake(false);
    setSuccess(false);

    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setShake(true);
      setLoading(false);
      setTimeout(() => setShake(false), 500);
      return;
    }

    try {
      // Prepare payload
      const payload = {
        Email: email,
        MobileNumber: "" // You might want to make this a separate field or remove if not needed
      };

      // Make API call
      const response = await postData("Authentication/forgot-password/send-otp", payload);
      
      if (response.isError === false) {
        // Success - OTP sent
        setSuccess(true);
        setError('');
        
        // Store user info for verification page if needed
        if (response.result) {
          localStorage.setItem('resetPasswordUser', JSON.stringify(response.result));
        }
        
        // Redirect to OTP verification page after a delay
        setTimeout(() => {
          navigate('/OtpVerify'); // Make sure this route exists
        }, 2000);
      } else {
        // Handle API error
        setError(response.message || 'Failed to send OTP. Please try again.');
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
    } catch (err) {
      // Handle network or other errors
      console.error('API Error:', err);
      setError(err.message || 'An error occurred. Please try again later.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 relative overflow-hidden">
      {/* Animated Bubbles */}
      <div className="absolute inset-0 overflow-hidden">
        {bubbles.map(bubble => (
          <div
            key={bubble.id}
            className="absolute rounded-full bg-gradient-to-br from-white/20 via-blue-200/15 to-purple-200/10 backdrop-blur-sm"
            style={{
              width: `${bubble.size}px`,
              height: `${bubble.size}px`,
              left: `${bubble.left}%`,
              top: '110%',
              opacity: bubble.opacity,
              filter: `blur(${bubble.blur}px)`,
              animation: `floatUp ${bubble.animationDuration}s ease-in-out ${bubble.animationDelay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-400/15 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className={`bg-white/95 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8 transition-all duration-300 ${shake ? 'animate-shake' : ''}`}>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-purple-50 shadow-lg border border-white/30">
                  {/* Lock Icon */}
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Reset Password
              </h1>
              <p className="text-blue-700 mt-2 text-sm">
                Enter your email to receive a verification code
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-3">
                <label className="text-sm font-medium text-blue-700 flex items-center">
                  {/* Mail Icon in Label */}
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-30 group-hover:opacity-40 transition duration-300" />
                  <div className="relative">
                    {/* Mail Icon inside Input - FIXED POSITIONING */}
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 z-30">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-blue-200 rounded-xl text-blue-900 placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 transition-all duration-300 relative z-20"
                      placeholder="you@example.com"
                      required
                      disabled={loading || success}
                    />
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm animate-fadeIn">
                  <div className="flex items-center">
                    {/* Alert Circle Icon */}
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm animate-fadeIn">
                  <div className="flex items-center">
                    {/* Check Circle Icon */}
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-semibold">Verification code sent!</p>
                      <p className="text-sm text-green-600 mt-1">
                        Check your email and phone for the OTP.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || success}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                <span className="relative flex items-center justify-center space-x-2">
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending Code...</span>
                    </>
                  ) : success ? (
                    <>
                      {/* Check Icon */}
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Code Sent!</span>
                    </>
                  ) : (
                    <>
                      {/* Send Icon */}
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Send Verification Code</span>
                    </>
                  )}
                </span>
              </button>
            </form>

            {/* Back to Login */}
            <div className="mt-2 pt-6 border-t border-blue-100/30">
              <Link
                to="/"
                className="inline-flex items-center justify-center w-full py-3 px-4 bg-white/50 hover:bg-white/70 text-blue-700 font-medium rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-300 group"
              >
                {/* Arrow Left Icon */}
                <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Login
              </Link>
            </div>

            {/* Copyright */}
            <div className="mt-2 pt-4 border-t border-blue-100/20 text-center">
              <p className="text-blue-600 text-xs">
                © {new Date().getFullYear()} Event Management System. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;